// export enum Routes {
//   HOMEPAGE="/operator",
//   TEAMS="/operator/teams",
//   GAMES="/operator/games",
//   STADIUM="/operator/stadium",
//   MAPPING="/operator/mapping",
//   MOMENT="/operator/moment",
// }

export enum Routes {
  HOMEPAGE="/",
  LOGIN="/",
  TEAMS="/teams",
  TEAM_DETAIL="/teams/:teamId",
  GAMES="/games",
  /** Single-game operator dashboard (matchup, engagement, downloads, etc.). */
  GAME_HUB="/games/hub/:gameId",
  STADIUM="/stadium",
  /** Deep-linkable venue seat manifest list for a stadium + seating configuration. */
  STADIUM_SEATS="/stadium/seats",
  MONITORING="/monitoring",
  VISION="/vision",
  MAPPING="/mapping",
  MOMENT="/moment",
  USERS="/users",
  GAMESEATS="/gameseats",
  TEMPLATES="/templates",
  SNAPS="/snaps",
  FILES="/files",
  FILES_MOMENTO="/files/momento",
  FILES_MOMENTO_FOLDER="/files/momento/:folderKey",
  FILES_TEAM="/files/teams/:teamId",
  FILES_TEAM_FOLDER="/files/teams/:teamId/:folderKey",
  SNAPS_GALLERY="/snaps/gallery",
  /** Public Snap Squad event recap form (tokenized, no login). */
  SQUAD_RECAP="/squad-recap/:token",
  SNAPS_RECAP="/snaps/recap/:token",
  /** Public parking expense form emailed after finish-game (tokenized, no login). */
  PARKING_EXPENSE="/parking-expense/:token",
  /** Public operator availability form (tokenized, no login). */
  AVAILABILITY="/availability/:token",
  SNAPS_TRACKER="/snaps/tracker",
  SNAPS_TRACKER_OTHER="/snaps/tracker/other",
  SNAPS_TRACKER_STADIUM="/snaps/tracker/:stadiumId",
  SNAPS_SQUAD="/snaps/squad",
  SNAPS_SQUAD_MEMBER="/snaps/squad/:memberId",
  /** SnapSquad film inventory table. */
  SNAPS_INVENTORY="/snaps/inventory",
  SNAPS_TEAMS="/snaps/teams",
  SNAPS_TEAM_DETAIL="/snaps/teams/:teamId",
  SNAPS_EVENT="/snaps/events/:eventId",
  CLIENT_TRACKER="/client-tracker",
  CLIENT_TRACKER_CONTACTS="/client-tracker/contacts",
  CLIENT_TRACKER_DETAIL="/client-tracker/:teamId",
  CROWD_ANALYTICS="/crowd-analytics",
  DOWNLOADS="/downloads",
  PRINTS="/prints",
  PROFILES="/profiles",
  INSIGHTS="/insights",
  ENGAGEMENT="/engagement",
  /** Engagement Funnel drop-off screen for a selected team. */
  ENGAGEMENT_FUNNEL="/engagement/funnel",
  /** Game recap report viewer (super admin). */
  GAME_RECAP="/recap",
  /** Client Services: generate seat crops across games. */
  SEAT_VIEW="/seat-view",
  /** Password-free public game / Momento recap share. */
  PUBLIC_GAME_RECAP="/public/recap/:token",
  /** Public team engagement dashboard (tokenized, no login). */
  PUBLIC_ENGAGEMENT="/public/engagement/:token",
  INDEXING="/indexing",
  TRIGGERING="/triggering",
  /** Standalone camera control popup (opened via window.open from Moments). */
  CAMERA_CONTROLS="/camera-controls",
  MANAGE_ACCESS="/manage-access",
  OPERATORS="/operators",
  /** Super-admin mass email / SMS to operators. */
  CONTACT_OPERATORS="/operators/contact",
  /** Super-admin game and operator scheduling (assignments, calendar). */
  SCHEDULING="/scheduling",
  /** Manage operators / SnapSquad roster for a Momento client team. */
  MANAGE_TEAM_OPERATORS="/scheduling/teams/:teamId/operators",
  /** Read-only operator schedule (non–super-admin). */
  SCHEDULE="/schedule",
  MANAGE_ACCESS_PROFILE="/manage-access/profile",
  SUPPORT="/support",
  ACCOUNT="/account",
  /** Super-admin notification center (profile menu). */
  NOTIFICATIONS="/notifications",
  /** Super-admin Tagboard PDK account management. */
  TAGBOARD_ACCOUNTS="/tagboard-accounts",
  /** Tagboard PDK OAuth login page (public — Tagboard redirects operators here). */
  TAGBOARD_OAUTH="/tagboard/oauth",
  /** Public videoboard carousel page for Tagboard iframe embedding. */
  VIDEOBOARD="/game/:gameId/videoboard",
  /** Super-admin payroll for operators, SnapSquad, and part-time staff. */
  EMPLOYEE_MANAGEMENT="/employee-management",
  /** Full roster payroll view (every employee, including $0 for the range). */
  EMPLOYEE_MANAGEMENT_ALL="/employee-management/all",
  /** Look up a seat/section across a team's game moments (Operations). */
  SEARCH_A_SEAT="/search-a-seat",
  /** Super-admin live board of games on a selected date. */
  LIVE_GAME_VIEW="/live-game-view",
  /** Owner-admin audit and activity logs (coming soon). */
  LOGGING="/logging",
  /** Owner-only unused Live_Moments image cleanup. */
  LIVE_MOMENTS_CLEANUP="/live-moments-cleanup",
}

export function gameHubPath(gameId: string): string {
  return Routes.GAME_HUB.replace(":gameId", encodeURIComponent(gameId));
}

export function manageTeamOperatorsPath(teamId: string): string {
  return Routes.MANAGE_TEAM_OPERATORS.replace(":teamId", encodeURIComponent(teamId));
}

export enum PARAMS {
  ID="id",
  TEAM_ID="teamId",
  /** Templates page: pre-select list filter from game graphics (home team). */
  TEMPLATE_TEAM_ID="templateTeamId",
  GAME_ID="gameId",
  OPEN_GENERAL_MOMENT_ID="openGeneralMomentId",
  STADIUM_ID="stadiumId",
  /** Seating config id for Seats List / venue manifest (does not change stadium active config). */
  CONFIG_ID="configId",
  STADIUM="stadium",
  CODE="code",
  HOSTNAME_PREFIX="hostnamePrefix",
  STATE="state",
  CANVAS_SIZE="canvasSize",
  /** Mapping page: auto-select section by name after camera load. */
  SECTION_NAME="sectionName",
  /** Open Stadiums monitoring modal (e.g. return from Venue Vision). */
  OPEN_MONITORING="openMonitoring",
  /** Open Stadiums cameras modal for STADIUM_ID (with stadiumId query param). */
  OPEN_STADIUM_CAMERAS="openStadiumCameras",
}

export function stadiumCamerasPath(stadiumId: string): string {
  const params = new URLSearchParams();
  params.set(PARAMS.STADIUM_ID, stadiumId);
  params.set(PARAMS.OPEN_STADIUM_CAMERAS, "1");
  return `${Routes.STADIUM}?${params.toString()}`;
}

export function cameraMappingPath(input: {
  cameraId: string;
  sectionName?: string;
  canvasSize?: number;
}): string {
  const params = new URLSearchParams();
  params.set(PARAMS.ID, input.cameraId);
  if (input.canvasSize) {
    params.set(PARAMS.CANVAS_SIZE, String(input.canvasSize));
  }
  if (input.sectionName) {
    params.set(PARAMS.SECTION_NAME, input.sectionName);
  }
  return `${Routes.MAPPING}?${params.toString()}`;
}

export function stadiumSeatsPath(
  stadiumId: string,
  configId: string,
  extras?: { status?: string },
): string {
  const params = new URLSearchParams();
  params.set(PARAMS.STADIUM_ID, stadiumId);
  params.set(PARAMS.CONFIG_ID, configId);
  if (extras?.status) {
    params.set("status", extras.status);
  }
  return `${Routes.STADIUM_SEATS}?${params.toString()}`;
}
